/**
 * Direct patient identifiers stripped from an eligibility-check export so the
 * benefits payload can be committed as a test fixture without PHI.
 *
 * Matched by key name anywhere in the structure: in this data these keys only
 * ever appear on a person (subscriber / member), never on a benefit, plan, or
 * payer row — so a blanket recursive scrub is safe and catches any nesting.
 * Plan/benefit/payer fields (planName, groupName, payer.name, cost-sharing,
 * effective dates) are intentionally preserved.
 *
 * Note: only *structured* identifier fields are redacted. Free-text fields
 * (e.g. an LLM `reasoning` string) are left as-is; they're derived from
 * benefits/practice context and aren't expected to contain patient names.
 */
const PII_KEYS = new Set<string>([
  'firstName', 'middleName', 'lastName',
  'dateOfBirth', 'dob',
  'memberId',
])

function scrub(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrub)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = PII_KEYS.has(k) ? null : scrub(v)
    }
    return out
  }
  return value
}

/**
 * Deep-clone `bundle` with all direct patient identifiers nulled out. Never
 * mutates the input. Benefit rows, plan info, and payer stay intact; only the
 * person-level name / DOB / member-ID fields are redacted.
 */
export function deidentifyEligibilityExport<T>(bundle: T): T {
  return scrub(bundle) as T
}
